import { Box } from '@factorialco/deprecated-design-system'
import { Widget } from '@factorialco/f0-react/dist/experimental'
import { isEmpty } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'

import { CustomizationField, fieldOptions } from 'modules/expenses/hooks/useExpensesCustomization'
import { Expensable } from 'modules/expenses/types/Expensable'

import { CustomTwoColumnsList } from '../CustomTwoColumnList'
import CustomWidgetHeaderButton from './CustomWidgetHeaderButton'

interface ExpensePaymentWidgetProps {
  expensable: Expensable
  customizationFields: CustomizationField[]
  editable: boolean
  onEdit: () => void
}

const calculatePaymentMethod = (
  creationType: string,
  payment: string,
  paymentMethod: string | undefined
): string => {
  if (!paymentMethod) {
    return ''
  }

  if (creationType === 'manual') {
    return paymentMethod
  } else {
    return i18n.t('expenses_v2.types.automatic')
  }
}

const calculateReimbursement = (
  locale: string,
  payment: string,
  reimbursableAmount: number,
  reimbursableCurrency: string
) => {
  if (payment === 'reimbursable') {
    return MoneyWithCurrency({
      amount: reimbursableAmount,
      currency: reimbursableCurrency,
      locale,
    })
  } else {
    return ''
  }
}

const buildPaymentInformationListData = (
  expensable: Expensable,
  locale: string,
  customizationFields: CustomizationField[]
) => {
  const creationType = expensable?.expense?.creationType ?? ''
  const payment = expensable?.expense?.payment ?? ''
  const paymentMethod = expensable?.expense?.paymentMethod ?? ''

  const reimbursableAmount = parseInt(expensable?.reimbursableAmount ?? '0')
  const reimbursableCurrency = (expensable?.reimbursableCurrency ?? '').toUpperCase()

  const paymentMethodOptions = fieldOptions('expense', 'payment_method', customizationFields)

  const paymentMethodValueLabel = paymentMethodOptions.find((el) => el.id === paymentMethod)?.label

  const items = [
    {
      title: i18n.t('expenses_v2.confirmation_screen.expense.payment_block.payment_method'),
      info: calculatePaymentMethod(creationType, payment, paymentMethodValueLabel),
    },
    {
      title: i18n.t('expenses_v2.confirmation_screen.expense.payment_block.total_reimbursement'),
      info: calculateReimbursement(
        locale,
        payment,
        reimbursableAmount,
        isCurrency(reimbursableCurrency) ? reimbursableCurrency : 'EUR'
      ),
    },
  ]

  return items.filter(({ info }) => !isEmpty(info))
}

const ExpensePaymentWidget = ({
  expensable,
  customizationFields,
  editable,
  onEdit,
}: ExpensePaymentWidgetProps) => {
  const { access } = useCurrentSession()
  const { locale } = access

  const list = buildPaymentInformationListData(expensable, locale, customizationFields)
  if (isEmpty(list)) {
    return null
  }

  return (
    <Box gap="s16" position="relative">
      <Widget
        header={{
          title: i18n.t('expenses_v2.confirmation_screen.expense.payment_block.title'),
        }}
      >
        <CustomTwoColumnsList list={list} />
      </Widget>
      {editable && (
        <CustomWidgetHeaderButton
          label={i18n.t('expenses_v2.confirmation_screen.confirmation_screen_block_edit_action')}
          onClick={onEdit}
        />
      )}
    </Box>
  )
}

export default ExpensePaymentWidget
